import { createContext, useContext, useState, useEffect, ReactNode } from "react";

/**
 * As tabelas de tradução saíram daqui em 20/10/2026.
 *
 * Elas eram 724 KB de fonte dentro deste arquivo, importado de forma síncrona
 * no App.tsx — ou seja, metade do bundle principal de 1.416 KB era a tradução
 * do site inteiro nos TRÊS idiomas, baixada por todo visitante antes de
 * qualquer pixel aparecer, para que ele usasse um terço dela.
 *
 * Agora cada idioma é um chunk próprio em src/i18n e só o ativo é baixado.
 *
 * Duas decisões que valem explicação:
 *
 * 1. O download começa na AVALIAÇÃO DO MÓDULO, não dentro de um efeito. Isso
 *    põe o chunk na rede enquanto o bundle principal ainda está executando e o
 *    React ainda não montou, em vez de serializar os dois. A aposta inicial lê
 *    a URL primeiro, porque é o que o I18nLayout vai acabar definindo de
 *    qualquer forma; localStorage e navegador são o desempate. Errar a aposta
 *    custa um chunk extra, não uma tela quebrada.
 *
 * 2. O provider NÃO renderiza os filhos antes da tabela chegar. A alternativa
 *    seria renderizar com texto vazio e deixar as palavras aparecerem depois,
 *    o que num site que é quase todo texto pareceria quebrado. Segurar não
 *    piora o que o usuário vê: hoje ele já olha para uma tela em branco até o
 *    React montar — isso só estende um pouco a mesma espera, em troca de 130 KB
 *    a menos no caminho crítico.
 */

const STORAGE_KEY = "arandia-language";

export type Language = "EN" | "PT" | "ES";

type Table = Record<string, string>;

const LOADERS: Record<Language, () => Promise<Table>> = {
  EN: () => import("@/i18n/en").then((m) => m.default),
  PT: () => import("@/i18n/pt").then((m) => m.default),
  ES: () => import("@/i18n/es").then((m) => m.default),
};

const cache: Partial<Record<Language, Table>> = {};
const inflight: Partial<Record<Language, Promise<Table>>> = {};

function loadTable(lang: Language): Promise<Table> {
  const hit = cache[lang];
  if (hit) return Promise.resolve(hit);
  if (!inflight[lang]) {
    inflight[lang] = LOADERS[lang]().then((table) => {
      cache[lang] = table;
      return table;
    });
  }
  return inflight[lang]!;
}

const getStoredLanguage = (): Language | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "EN" || stored === "PT" || stored === "ES") return stored;
  } catch {
    // localStorage lança em janela privada e em webview de app
  }
  return null;
};

const getLocaleFromUrl = (): Language | null => {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/(pt|es|en)(?=\/|$)/i);
  if (!m) return null;
  const code = m[1].toLowerCase();
  return code === "pt" ? "PT" : code === "es" ? "ES" : "EN";
};

const detectBrowserLanguage = (): Language => {
  if (typeof window === "undefined") return "EN";
  const nav = navigator as Navigator & { userLanguage?: string };
  const lang = (nav.language || nav.userLanguage || "").toLowerCase();
  if (lang.startsWith("pt")) return "PT";
  if (lang.startsWith("es")) return "ES";
  return "EN";
};

/** A URL manda: é o que o I18nLayout vai aplicar de qualquer jeito. */
const initialLanguage = (): Language =>
  getLocaleFromUrl() ?? getStoredLanguage() ?? detectBrowserLanguage();

// Dispara já, na importação do módulo, para o chunk viajar em paralelo.
if (typeof window !== "undefined") {
  void loadTable(initialLanguage());
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// During development, editing this file must trigger a full reload.
// A partial hot update creates a second context instance while mounted
// consumers still hold the old one, which throws the provider error above.
if (import.meta.hot) {
  import.meta.hot.invalidate();
}

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  // Se o chunk já chegou antes do React montar — que é o caso comum, já que o
  // download começou na avaliação do módulo — nasce preenchido e não há espera.
  const [table, setTable] = useState<Table | null>(() => cache[language] ?? null);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // sem storage: a escolha vale para a sessão atual
    }
  };

  useEffect(() => {
    let alive = true;
    loadTable(language).then((loaded) => {
      if (alive) setTable(loaded);
    });
    return () => {
      alive = false;
    };
  }, [language]);

  const t = (key: string): string => table?.[key] || key;

  // Sem tabela não há o que renderizar que não seja texto faltando.
  if (!table) return null;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
